import { MapService } from '../map/mapService';
import { UIService } from '../ui/uiService';
import { getParcelGeoJSON, getCoordinates, calcularBoundingBox } from './cadastralToolsService';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { Feature } from 'ol';

// Tipos para el servicio catastral
export interface CadastralConfig {
  defaultRefCat?: string;
  enableAutoCenter?: boolean;
  enableNotifications?: boolean;
  enableCaching?: boolean;
  cacheExpiration?: number; // en minutos
  styleOptions?: {
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    opacity?: number;
  };
}

export interface CadastralParcel {
  refCat: string;
  coordinates: number[][];
  area?: number;
  properties?: Record<string, any>;
  boundingBox?: number[];
  lastUpdated: Date;
}

export interface CadastralState {
  currentParcel: CadastralParcel | null;
  lastRefCat: string | null;
  isVisible: boolean;
  isLoading: boolean;
  error: string | null;
  cache: Map<string, CadastralParcel>;
}

export interface CadastralService {
  // Métodos principales
  init(): Promise<void>;
  destroy(): Promise<void>;
  
  // Gestión de parcelas
  searchParcel(refCat: string): Promise<CadastralParcel | null>;
  showParcel(refCat: string): Promise<boolean>;
  hideParcel(): void;
  toggleParcel(): Promise<boolean>;
  clearParcel(): void;
  
  // Información de parcelas
  getCurrentParcel(): CadastralParcel | null;
  getParcelCoordinates(refCat: string): Promise<{ lon: number; lat: number } | null>;
  getParcelArea(refCat: string): Promise<number | null>;
  getParcelBoundingBox(refCat: string): Promise<number[] | null>;
  
  // Gestión de capas
  getParcelLayer(): VectorLayer<VectorSource>;
  setParcelStyle(style: Style): void;
  updateParcelStyle(options: Partial<CadastralConfig['styleOptions']>): void;
  
  // Estado y configuración
  getState(): CadastralState;
  getConfig(): CadastralConfig;
  updateConfig(config: Partial<CadastralConfig>): void;
  
  // Utilidades
  validateRefCat(refCat: string): boolean;
  formatRefCat(refCat: string): string;
  exportParcelData(format: 'geojson' | 'csv' | 'json'): string | null;
}

// Implementación del servicio catastral
export function createCadastralService(
  mapService: MapService,
  uiService?: UIService,
  config: CadastralConfig = {}
): CadastralService {
  
  // Configuración por defecto
  const defaultConfig: CadastralConfig = {
    defaultRefCat: undefined, // Sin referencia catastral por defecto
    enableAutoCenter: true,
    enableNotifications: true,
    enableCaching: true,
    cacheExpiration: 30, // 30 minutos
    styleOptions: {
      fillColor: 'rgba(0, 128, 0, 0.4)',
      strokeColor: 'green',
      strokeWidth: 2,
      opacity: 1
    }
  };
  
  const finalConfig = { ...defaultConfig, ...config };
  
  // Estado del servicio
  let state: CadastralState = {
    currentParcel: null,
    lastRefCat: null,
    isVisible: false,
    isLoading: false,
    error: null,
    cache: new Map()
  };
  
  // Capa de parcela: reutiliza la capa del MapService si existe
  let parcelLayer: VectorLayer<VectorSource>;
  const existingLayers = mapService.getLayers();
  if (existingLayers && existingLayers.parcelaLayer) {
    parcelLayer = existingLayers.parcelaLayer as VectorLayer<VectorSource>;
  } else {
    parcelLayer = new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        fill: new Fill({ color: finalConfig.styleOptions!.fillColor }),
        stroke: new Stroke({ 
          color: finalConfig.styleOptions!.strokeColor!,
          width: finalConfig.styleOptions!.strokeWidth!
        })
      }),
      zIndex: 0
    });
  }
  
  // Función para validar REFCAT
  function validateRefCat(refCat: string): boolean {
    if (!refCat || typeof refCat !== 'string') return false;
    // Aceptar REFCAT de 14 a 20 caracteres alfanuméricos (ej. 5891801UM5059B)
    const cleaned = refCat.replace(/\s/g, '').toUpperCase();
    const refCatPattern = /^[A-Z0-9]{14,20}$/;
    return refCatPattern.test(cleaned);
  }
  
  // Función para formatear REFCAT
  function formatRefCat(refCat: string): string {
    // Normalizar: quitar espacios y pasar a mayúsculas, sin añadir separadores
    return refCat.replace(/\s/g, '').toUpperCase();
  }
  
  // Función para obtener coordenadas de parcela
  async function getParcelCoordinates(refCat: string): Promise<{ lon: number; lat: number } | null> {
    try {
      return await getCoordinates(refCat);
    } catch (error) {
      console.error('Error al obtener coordenadas de parcela:', error);
      return null;
    }
  }
  
  // Función para calcular área de parcela
  async function getParcelArea(refCat: string): Promise<number | null> {
    try {
      const geojson = await getParcelGeoJSON(refCat);
      if (!geojson || !geojson.features || geojson.features.length === 0) {
        return null;
      }
      
      // Calcular área usando la fórmula de Gauss
      const coordinates = geojson.features[0].geometry.coordinates[0];
      let area = 0;
      
      for (let i = 0; i < coordinates.length - 1; i++) {
        const [x1, y1] = coordinates[i];
        const [x2, y2] = coordinates[i + 1];
        area += (x1 * y2 - x2 * y1);
      }
      
      return Math.abs(area) / 2;
    } catch (error) {
      console.error('Error al calcular área de parcela:', error);
      return null;
    }
  }
  
  // Función para obtener bounding box de parcela
  async function getParcelBoundingBox(refCat: string): Promise<number[] | null> {
    try {
      const geojson = await getParcelGeoJSON(refCat);
      return calcularBoundingBox(geojson);
    } catch (error) {
      console.error('Error al obtener bounding box de parcela:', error);
      return null;
    }
  }
  
  // Función para buscar parcela
  async function searchParcel(refCat: string): Promise<CadastralParcel | null> {
    if (!validateRefCat(refCat)) {
      throw new Error('Referencia catastral inválida');
    }
    
    const formattedRefCat = formatRefCat(refCat);
    
    // Verificar caché
    if (finalConfig.enableCaching && state.cache.has(formattedRefCat)) {
      const cached = state.cache.get(formattedRefCat)!;
      const now = new Date();
      const expirationTime = new Date(cached.lastUpdated.getTime() + finalConfig.cacheExpiration! * 60 * 1000);
      
      if (now < expirationTime) {
        return cached;
      } else {
        state.cache.delete(formattedRefCat);
      }
    }
    
    try {
      state.isLoading = true;
      state.error = null;
      
      if (uiService && finalConfig.enableNotifications) {
        uiService.showNotification('Buscando parcela catastral...', 'info');
      }
      
      // Obtener GeoJSON de la parcela
      const geojson = await getParcelGeoJSON(formattedRefCat);
      
      if (!geojson || !geojson.features || geojson.features.length === 0) {
        throw new Error('No se encontró la parcela catastral');
      }
      
      // Extraer coordenadas
      const coordinates = geojson.features[0].geometry.coordinates[0];
      
      // Calcular área y bounding box
      const area = await getParcelArea(formattedRefCat);
      const boundingBox = calcularBoundingBox(geojson);
      
      // Crear objeto de parcela
      const parcel: CadastralParcel = {
        refCat: formattedRefCat,
        coordinates,
        area,
        properties: geojson.features[0].properties || {},
        boundingBox,
        lastUpdated: new Date()
      };
      
      // Guardar en caché
      if (finalConfig.enableCaching) {
        state.cache.set(formattedRefCat, parcel);
      }
      
      state.currentParcel = parcel;
      state.lastRefCat = formattedRefCat;
      
      if (uiService && finalConfig.enableNotifications) {
        uiService.showNotification('Parcela encontrada correctamente', 'success');
      }
      
      return parcel;
      
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error desconocido';
      
      if (uiService && finalConfig.enableNotifications) {
        uiService.showNotification(`Error al buscar parcela: ${state.error}`, 'error');
      }
      
      throw error;
    } finally {
      state.isLoading = false;
    }
  }
  
  // Función para mostrar parcela en el mapa
  async function showParcel(refCat: string): Promise<boolean> {
    try {
      const parcel = await searchParcel(refCat);
      if (!parcel) return false;
      
      // Limpiar capa anterior
      parcelLayer.getSource()?.clear();
      
      // Convertir GeoJSON a features de OpenLayers
      const format = new GeoJSON();
      const geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [parcel.coordinates]
          },
          properties: parcel.properties
        }]
      };
      
      const features = format.readFeatures(geojson, {
        featureProjection: 'EPSG:3857'
      });
      
      // Añadir features a la capa
      parcelLayer.getSource()?.addFeatures(features);
      
      // Centrar mapa en la parcela
      if (finalConfig.enableAutoCenter && parcel.boundingBox) {
        const map = mapService.getMap();
        if (map) {
          const extent = parcelLayer.getSource()?.getExtent();
          if (extent) {
            map.getView().fit(extent, { duration: 1000 });
          }
        }
      }
      
      state.isVisible = true;
      
      if (uiService && finalConfig.enableNotifications) {
        uiService.showNotification('Parcela mostrada en el mapa', 'success');
      }
      
      return true;
      
    } catch (error) {
      console.error('Error al mostrar parcela:', error);
      return false;
    }
  }
  
  // Función para ocultar parcela
  function hideParcel(): void {
    parcelLayer.setVisible(false);
    state.isVisible = false;
    
    if (uiService && finalConfig.enableNotifications) {
      uiService.showNotification('Parcela ocultada', 'info');
    }
  }
  
  // Función para alternar visibilidad de parcela
  async function toggleParcel(): Promise<boolean> {
    if (!state.currentParcel) {
      if (state.lastRefCat) {
        return await showParcel(state.lastRefCat);
      }
      return false;
    }
    
    if (state.isVisible) {
      hideParcel();
      return false;
    } else {
      // Asegurar que haya geometría si la fuente está vacía
      const src = parcelLayer.getSource();
      if (src && src.getFeatures().length === 0 && state.currentParcel) {
        const format = new GeoJSON();
        const geojson = {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: { type: 'Polygon', coordinates: [state.currentParcel.coordinates] },
            properties: state.currentParcel.properties
          }]
        };
        const features = format.readFeatures(geojson, { featureProjection: 'EPSG:3857' });
        src.addFeatures(features);
      }
      parcelLayer.setVisible(true);
      state.isVisible = true;
      
      if (uiService && finalConfig.enableNotifications) {
        uiService.showNotification('Parcela mostrada', 'success');
      }
      
      return true;
    }
  }
  
  // Función para limpiar parcela
  function clearParcel(): void {
    parcelLayer.getSource()?.clear();
    state.currentParcel = null;
    state.isVisible = false;
    state.error = null;
    
    if (uiService && finalConfig.enableNotifications) {
      uiService.showNotification('Parcela eliminada', 'info');
    }
  }
  
  // Función para actualizar estilo de parcela
  function updateParcelStyle(options: Partial<CadastralConfig['styleOptions']>): void {
    const currentStyle = parcelLayer.getStyle() as Style;
    const fill = currentStyle.getFill();
    const stroke = currentStyle.getStroke();
    
    const newStyle = new Style({
      fill: new Fill({ 
        color: options.fillColor || fill?.getColor() || finalConfig.styleOptions!.fillColor 
      }),
      stroke: new Stroke({ 
        color: options.strokeColor || stroke?.getColor() || finalConfig.styleOptions!.strokeColor!,
        width: options.strokeWidth || stroke?.getWidth() || finalConfig.styleOptions!.strokeWidth!
      })
    });
    
    parcelLayer.setStyle(newStyle);
  }
  
  // Función para exportar datos de parcela
  function exportParcelData(format: 'geojson' | 'csv' | 'json'): string | null {
    if (!state.currentParcel) return null;
    
    switch (format) {
      case 'geojson':
        return JSON.stringify({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [state.currentParcel.coordinates]
            },
            properties: {
              refCat: state.currentParcel.refCat,
              area: state.currentParcel.area,
              ...state.currentParcel.properties
            }
          }]
        }, null, 2);
        
      case 'csv':
        const headers = ['refCat', 'area', 'lastUpdated'];
        const values = [
          state.currentParcel.refCat,
          state.currentParcel.area || '',
          state.currentParcel.lastUpdated.toISOString()
        ];
        return `${headers.join(',')}\n${values.join(',')}`;
        
      case 'json':
        return JSON.stringify(state.currentParcel, null, 2);
        
      default:
        return null;
    }
  }
  
  // Retornar el servicio
  return {
    // Métodos principales
    async init(): Promise<void> {
      try {
        // Añadir la capa al mapa si no estaba ya incluida
        const map = mapService.getMap();
        if (map) {
          const alreadyAdded = map.getLayers().getArray().includes(parcelLayer as any);
          if (!alreadyAdded) map.addLayer(parcelLayer);
        }
        
        // Mostrar parcela por defecto si está configurada
        if (finalConfig.defaultRefCat) {
          await showParcel(finalConfig.defaultRefCat);
        }
        
        if (uiService && finalConfig.enableNotifications) {
          uiService.showNotification('CadastralService inicializado correctamente', 'success');
        }
      } catch (error) {
        console.error('Error al inicializar CadastralService:', error);
        throw error;
      }
    },
    
    async destroy(): Promise<void> {
      try {
        // Limpiar caché
        state.cache.clear();
        
        // Remover capa del mapa
        const map = mapService.getMap();
        if (map) {
          map.removeLayer(parcelLayer);
        }
        
        // Limpiar estado
        state = {
          currentParcel: null,
          lastRefCat: null,
          isVisible: false,
          isLoading: false,
          error: null,
          cache: new Map()
        };
        
        if (uiService && finalConfig.enableNotifications) {
          uiService.showNotification('CadastralService destruido', 'info');
        }
      } catch (error) {
        console.error('Error al destruir CadastralService:', error);
        throw error;
      }
    },
    
    // Gestión de parcelas
    searchParcel,
    showParcel,
    hideParcel,
    toggleParcel,
    clearParcel,
    
    // Información de parcelas
    getCurrentParcel: () => state.currentParcel,
    getParcelCoordinates,
    getParcelArea,
    getParcelBoundingBox,
    
    // Gestión de capas
    getParcelLayer: () => parcelLayer,
    setParcelStyle: (style: Style) => parcelLayer.setStyle(style),
    updateParcelStyle,
    
    // Estado y configuración
    getState: () => ({ ...state }),
    getConfig: () => ({ ...finalConfig }),
    updateConfig: (newConfig: Partial<CadastralConfig>) => {
      Object.assign(finalConfig, newConfig);
      if (newConfig.styleOptions) {
        updateParcelStyle(newConfig.styleOptions);
      }
    },
    
    // Utilidades
    validateRefCat,
    formatRefCat,
    exportParcelData
  };
}
